import { FC } from 'react';
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrency, formatNumber } from 'utils/formats';
import { dateFormat } from 'constants/formats';
import {
  IncentiveOfferOperation,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';

import { statusBadgeMap } from '../../../constants/incentiveOfferOperationStatus';
import getOperationReference from '../../../utils/getOperationReference';

type IncentiveOfferOperationsTableProps = {
  operations: IncentiveOfferOperation[];
  onGoToOperation: (operationId: string) => void;
};

const getBuyerName = (buyer: string | Partial<IncentivesMarketBeneficiary>) => {
  if (typeof buyer === 'string') return buyer;
  return buyer?.name || '';
};

const IncentiveOfferOperationsTable: FC<IncentiveOfferOperationsTableProps> = ({
  operations,
  onGoToOperation,
}) => {
  const { t } = useTranslate();

  if (operations.length === 0) return null;

  const completedCount = operations.filter(
    (op) => op.status === 'COMPLETED'
  ).length;
  const pendingCount = operations.length - completedCount;
  const totalOperatedAmount = operations.reduce(
    (sum, op) => sum + op.totalPrice,
    0
  );

  return (
    <div className="card card-bordered mb-4">
      <div className="card-inner">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <h6 className="card-title mb-0">
            <em className="icon ni ni-list-check text-primary mr-2" />
            {t('OperationStatus')}
          </h6>
        </div>

        <p className="text-soft fs-13px mb-3">
          {t('OperationsTableDescription')}
        </p>

        <div className="table-responsive">
          <table className="table table-tranx table-hover mb-0">
            <thead>
              <tr className="tb-tnx-head">
                <th className="tb-tnx-id">
                  <span className="d-none d-md-inline">
                    {t('OperationRef')}
                  </span>
                </th>
                <th>
                  <span className="d-none d-md-inline">{t('Buyer')}</span>
                </th>
                <th>
                  <span className="d-none d-md-inline">{t('IncentivesMarketUnits')}</span>
                </th>

                <th>
                  <span className="d-none d-md-inline">{t('Amount')}</span>
                </th>
                <th>
                  <span className="d-none d-md-inline">{t('Date')}</span>
                </th>
                <th>
                  <span className="d-none d-md-inline">{t('Status')}</span>
                </th>
                <th className="tb-tnx-action text-right" />
              </tr>
            </thead>
            <tbody>
              {operations.map((op) => {
                const badge = statusBadgeMap[op.status] || {
                  className: 'badge-secondary',
                  label: op.status,
                };

                return (
                  <tr
                    key={op._id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onGoToOperation(op._id)}
                  >
                    <td className="tb-tnx-id">
                      <span className="ref text-base badge badge-light">
                        {getOperationReference(op)}
                      </span>
                    </td>
                    <td>
                      <span className="d-inline-flex align-items-center">
                        <em className="icon ni ni-user mr-1 text-soft" />
                        {getBuyerName(op.buyer)}
                      </span>
                    </td>
                    <td>{formatNumber(op.unitsCount)}</td>
                    <td>
                      <strong>{formatCurrency(op.totalPrice)}</strong>
                    </td>
                    <td>{format(new Date(op.requestDate), dateFormat)}</td>
                    <td>
                      <span className={`badge badge-dim ${badge.className}`}>
                        {t(badge.label)}
                      </span>
                    </td>
                    <td className="tb-tnx-action text-right">
                      <button
                        type="button"
                        className="btn btn-icon btn-trigger btn-sm"
                        title={t('ViewDetail')}
                        onClick={(e) => {
                          e.stopPropagation();
                          onGoToOperation(op._id);
                        }}
                      >
                        <em className="icon ni ni-arrow-right" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-3 pt-3 border-top">
          <p className="text-soft fs-12px mb-0">
            <em className="icon ni ni-info mr-1" />
            {t('TotalOperations')}: <strong>{operations.length}</strong> ·{' '}
            {t('Completed')}: {completedCount} · {t('Pending')}: {pendingCount}{' '}
            · {t('TotalOperatedAmount')}:{' '}
            <strong>{formatCurrency(totalOperatedAmount)}</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export default IncentiveOfferOperationsTable;
